import React, { useState, useEffect } from 'react';
import { Container, LeftSection, RightSection, BrandLogo, Title, Subtitle, RobotImage, Form, Input, LoginButton, Divider, MicrosoftButton } from './loginPageStyles';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Clear any existing token on component mount (clean slate for login)
  useEffect(() => {
    // Check if we have a token in URL (from Microsoft SSO callback)
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    const errorFromUrl = urlParams.get('error');

    if (tokenFromUrl) {
      // Store token from Microsoft SSO and redirect
      localStorage.clear();
      localStorage.setItem('token', tokenFromUrl);
      // Extract username from URL if provided, otherwise use email from token
      window.location.href = '/translation';
      return;
    }

    if (errorFromUrl) {
      setError(`Microsoft login failed: ${errorFromUrl}`);
    }

    // Otherwise clear localStorage for fresh login
    if (!tokenFromUrl && !errorFromUrl) {
      localStorage.clear();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Clear localStorage before login to ensure clean state
    localStorage.clear();

    try {
      const response = await fetch('/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username,
          password,
        }),
      });

      if (response.status === 401) {
        setError('Incorrect username or password');
        setIsLoading(false);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const token = data.access_token;

        // Store token and username in localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('username', username);

        // Force page reload to ensure TranslationPage gets fresh token
        window.location.href = '/translation';
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Login failed');
        setIsLoading(false);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    // Redirect to backend Microsoft SSO endpoint
    // In development, proxy doesn't work for window.location, so use full backend URL
    // In production, use relative path (proxied by nginx)
    const isProduction = process.env.NODE_ENV === 'production';
    const backendUrl = isProduction ? '' : 'http://localhost:5000';
    window.location.href = `${backendUrl}/auth/microsoft/login`;
  };

  return (
    <Container>
      <LeftSection>
        <BrandLogo src="wavestone-logo.png" alt="Brand Logo" />
        <Title>Welcome to WaveTranslate</Title>
        <Subtitle>
          Secure document and text translation with interchangeable LLM models.
          Translate your files with complete confidentiality.
        </Subtitle>
        <Form onSubmit={handleLogin}>
          <Input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={isLoading}
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
          <LoginButton type="submit" disabled={isLoading}>
            {isLoading ? 'Logging in...' : 'Log in'}
          </LoginButton>
          {error && <p style={{ color: '#dc3545', marginTop: '10px', fontSize: '14px' }}>{error}</p>}

          <Divider>or</Divider>

          <MicrosoftButton type="button" onClick={handleMicrosoftLogin} disabled={isLoading}>
            <svg viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            Sign in with Microsoft
          </MicrosoftButton>
        </Form>
      </LeftSection>
      <RightSection>
        <RobotImage src="wavebot-with-background.png" alt="Robot" />
      </RightSection>
    </Container>
  );
};

export default LoginPage;
